import { useCallback, useEffect, useRef, useMemo } from 'react';
import { eachDayOfInterval, format, parseISO } from 'date-fns';
import { useTimesheetStore } from '../stores/timesheetStore';
import { useUsersStore } from '../stores/usersStore';
import { getGroupingConfig } from '../config/timesheet-grouping';
import {
  getTimesheetTimeline,
  getTimesheetCapacities,
  type TimesheetAggRow,
} from '../api/timesheet.api';
import {
  buildTimesheetTree,
  type LabelResolvers,
} from '../utils/timesheet-tree-builder';
import { buildCapacityMap } from '../utils/timesheet-capacity';
import { fetchBoardsByIds } from '../api/monday.api';
import mondaySdk from 'monday-sdk-js';

/**
 * Orchestrates data fetching for the timeline timesheet.
 *
 * Name caches (board names, item names) live in the Zustand store so they
 * survive view switches and don't trigger re-fetch cascades when updated.
 * The fetch callback reads caches via refs to avoid dependency loops.
 */
export function useTimesheetData(): void {
  const {
    groupBy,
    subGroupBy,
    dateRange,
    filters,
    setRows,
    setCapacityMap,
    setDateColumns,
    setLoading,
    setError,
    boardNameMap,
    itemNameMap,
    mergeBoardNames,
    mergeItemNames,
  } = useTimesheetStore();

  const users = useUsersStore((s) => s.users);

  // Build user map for name resolution
  const userMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const u of users) {
      map.set(String(u.id), u.name);
    }
    return map;
  }, [users]);

  // ── Refs for values read during fetch but NOT fetch triggers ──────────
  // These break the dependency cycle: name cache updates don't re-trigger fetchData.
  const boardNameMapRef = useRef(boardNameMap);
  boardNameMapRef.current = boardNameMap;

  const itemNameMapRef = useRef(itemNameMap);
  itemNameMapRef.current = itemNameMap;

  const userMapRef = useRef(userMap);
  userMapRef.current = userMap;

  // Abort controller for cancelling in-flight requests
  const abortRef = useRef<AbortController | null>(null);

  const fetchData = useCallback(async () => {
    // Always abort any previous in-flight request first (e.g. when switching views)
    abortRef.current?.abort();

    // Only fetch when in timeline view mode (not progress)
    const currentConfig = getGroupingConfig(groupBy);
    if (currentConfig.viewMode !== 'timeline') return;

    // Don't fetch if date range is empty
    if (!dateRange.start || !dateRange.end) return;

    // Don't fetch if no users/teams are selected — require explicit selection
    if (filters.userIds.length === 0) {
      setRows([]);
      setCapacityMap({});
      setLoading(false);
      return;
    }

    const config = getGroupingConfig(groupBy);

    // Create new abort controller for this request
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);

    try {
      // 1. Build date columns
      const startDate = parseISO(dateRange.start);
      const endDate = parseISO(dateRange.end);
      const daysList = eachDayOfInterval({ start: startDate, end: endDate });
      const dateColumns = daysList.map((d) => format(d, 'yyyy-MM-dd'));
      setDateColumns(dateColumns);

      // 2. Fetch aggregated worklog data
      const resp = await getTimesheetTimeline(
        {
          startDate: dateRange.start,
          endDate: dateRange.end,
          groupBy: config.backendGroupBy,
          userIds:
            filters.userIds.length > 0 ? filters.userIds : undefined,
          boardIds:
            filters.boardIds.length > 0 ? filters.boardIds : undefined,
          subGroupBy: subGroupBy ?? undefined,
        },
        controller.signal,
      );

      if (controller.signal.aborted) return;

      // 2b. Resolve board names for any board IDs in the response
      const currentBoardMap = boardNameMapRef.current;
      if (resp.rows.length > 0) {
        const unknownBoardIds: string[] = [];
        for (const row of resp.rows) {
          if (row.groupKey && !currentBoardMap.has(row.groupKey)) {
            unknownBoardIds.push(row.groupKey);
          }
          if (row.childKey && !currentBoardMap.has(row.childKey)) {
            unknownBoardIds.push(row.childKey);
          }
        }
        const uniqueIds = [...new Set(unknownBoardIds)].filter(
          (id) => /^\d+$/.test(id),
        );
        if (uniqueIds.length > 0) {
          try {
            const boards = await fetchBoardsByIds(uniqueIds);
            if (!controller.signal.aborted && boards.length > 0) {
              mergeBoardNames(boards.map((b) => ({ id: b.id, name: b.name })));
            }
          } catch {
            // Board name resolution is non-critical
          }
          if (controller.signal.aborted) return;
        }
      }

      // 3. Resolve item names if in 'item' grouping mode
      const currentItemMap = itemNameMapRef.current;
      if (groupBy === 'item' && resp.rows.length > 0) {
        const unknownItemIds = new Set<string>();
        for (const row of resp.rows) {
          if (row.childKey && !currentItemMap.has(row.childKey)) {
            unknownItemIds.add(row.childKey);
          }
        }

        if (unknownItemIds.size > 0) {
          try {
            const monday = mondaySdk();
            const itemIds = Array.from(unknownItemIds).slice(0, 100);
            const query = `
              query GetItems($ids: [ID!]!) {
                items(ids: $ids) {
                  id
                  name
                }
              }
            `;
            const response = await monday.api(query, {
              variables: { ids: itemIds },
            });
            const data = response.data as {
              items?: Array<{ id: string | number; name: string }>;
            };
            if (!controller.signal.aborted && data.items) {
              mergeItemNames(
                data.items.map((item) => ({
                  id: String(item.id),
                  name: item.name,
                })),
              );
            }
          } catch (err) {
            console.error('Failed to fetch item names:', err);
          }
          if (controller.signal.aborted) return;
        }
      }

      // 4. Fetch capacity for ALL selected users (not just those with worklogs)
      const selectedUserIds = filters.userIds;
      let capMap = {};
      if (selectedUserIds.length > 0) {
        const caps = await getTimesheetCapacities(
          {
            startDate: dateRange.start,
            endDate: dateRange.end,
            userIds: selectedUserIds,
          },
          controller.signal,
        );
        if (controller.signal.aborted) return;
        capMap = buildCapacityMap(caps.capacities);
      }

      // 5. Build tree with capacity data
      // Read latest name maps from refs (may have been updated above)
      const latestUserMap = userMapRef.current;
      const latestBoardMap = boardNameMapRef.current;
      const latestItemMap = itemNameMapRef.current;

      const resolvers: LabelResolvers = {
        resolveUser: (userId: string) =>
          latestUserMap.get(userId) ?? `User #${userId}`,
        resolveBoard: (boardId: string) =>
          latestBoardMap.get(boardId) ?? `Board #${boardId}`,
        resolveItem: (itemId: string) =>
          latestItemMap.get(itemId) ?? `Item #${itemId}`,
      };

      const finalTree = buildTimesheetTree(
        resp.rows,
        groupBy,
        resolvers,
        capMap,
        dateColumns,
        selectedUserIds,
        subGroupBy,
      );

      setRows(finalTree);
      setCapacityMap(capMap);
    } catch (err) {
      if (controller.signal.aborted) return;
      const message =
        err instanceof Error ? err.message : 'Failed to load timesheet data';
      setError(message);
      console.error('Timesheet fetch error:', err);
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    }
  }, [
    groupBy,
    subGroupBy,
    dateRange,
    filters,
    setRows,
    setCapacityMap,
    setDateColumns,
    setLoading,
    setError,
    mergeBoardNames,
    mergeItemNames,
  ]);

  // Fetch on mount and when dependencies change
  useEffect(() => {
    fetchData();
    return () => {
      abortRef.current?.abort();
    };
  }, [fetchData]);
}
